import { Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { UpcomingEventsTable } from "@/components/dashboard/UpcomingEventsTable"
import { useQuery } from "@tanstack/react-query"
import { getUpcomingCorporateEvents } from "@/lib/queries/events"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

export default function Events({ profile, session }: { profile: any | null, session: any | null }) {
  const navigate = useNavigate()
  const [upcomingPage, setUpcomingPage] = useState(1)
  const [completedPage, setCompletedPage] = useState(1)
  const [cancelledPage, setCancelledPage] = useState(1)
  const [activeTab, setActiveTab] = useState("upcoming")

  const { data: upcomingResponse = { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1 } }, isLoading: upcomingLoading } = useQuery({
    queryKey: ["events", "upcoming", profile?.id, upcomingPage],
    queryFn: () => getUpcomingCorporateEvents(session?.access_token!, 10, upcomingPage, ["payment_pending", "event_booked", "event_confirmed", "event_in_progress"]),
    enabled: !!profile?.id && !!session?.access_token,
  })

  const { data: completedResponse = { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1 } }, isLoading: completedLoading } = useQuery({
    queryKey: ["events", "completed", profile?.id, completedPage],
    queryFn: () => getUpcomingCorporateEvents(session?.access_token!, 10, completedPage, ["event_complete"]),
    enabled: !!profile?.id && !!session?.access_token,
  })

  const { data: cancelledResponse = { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1 } }, isLoading: cancelledLoading } = useQuery({
    queryKey: ["events", "cancelled", profile?.id, cancelledPage],
    queryFn: () => getUpcomingCorporateEvents(session?.access_token!, 10, cancelledPage, ["event_cancelled"]),
    enabled: !!profile?.id && !!session?.access_token,
  })

  const upcomingEvents = upcomingResponse.data || []
  const completedEvents = completedResponse.data || []
  const cancelledEvents = cancelledResponse.data || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Events</h1>
        <Button 
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={() => navigate("/opportunities/")}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Event
        </Button>
      </div>


      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="upcoming">
            Upcoming Events {upcomingResponse.pagination.total > 0 && `(${upcomingResponse.pagination.total})`}
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed Events {completedResponse.pagination.total > 0 && `(${completedResponse.pagination.total})`}
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled Events {cancelledResponse.pagination.total > 0 && `(${cancelledResponse.pagination.total})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4">
          {upcomingLoading ? (
            <div className="text-center">Loading events...</div>
          ) : upcomingEvents.length > 0 ? (
            <UpcomingEventsTable 
              events={upcomingEvents} 
              profile={profile} 
              session={session} 
              pagination={upcomingResponse.pagination}
              onPageChange={(page) => setUpcomingPage(page)}
            />
          ) : (
            <div className="text-center text-muted-foreground">No upcoming events found</div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedLoading ? (
            <div className="text-center">Loading events...</div>
          ) : completedEvents.length > 0 ? (
            <UpcomingEventsTable 
              events={completedEvents} 
              profile={profile} 
              session={session} 
              pagination={completedResponse.pagination}
              onPageChange={(page) => setCompletedPage(page)}
            />
          ) : (
            <div className="text-center text-muted-foreground">No completed events found</div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="space-y-4">
          {cancelledLoading ? (
            <div className="text-center">Loading events...</div>
          ) : cancelledEvents.length > 0 ? (
            <UpcomingEventsTable 
              events={cancelledEvents} 
              profile={profile} 
              session={session} 
              pagination={cancelledResponse.pagination}
              onPageChange={(page) => setCancelledPage(page)}
            />
          ) : (
            <div className="text-center text-muted-foreground">No cancelled events found</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}